import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  increment,
} from 'firebase/firestore'
import { db } from './config'
import type { CV } from '../../types/cv'
import type { TemplateType } from '../../utils/colorPalettes'
import type { PublicCV } from '../../types/publicCv'
import { slugify } from '../../utils/slugify'
import { nanoid } from 'nanoid'

const COLLECTION = 'publicCVs'

/** Check if a slug is available */
export async function checkSlugAvailability(slug: string): Promise<boolean> {
  const q = query(collection(db, COLLECTION), where('slug', '==', slug), where('enabled', '==', true))
  const snapshot = await getDocs(q)
  return snapshot.empty
}

/** Publish a CV */
export async function publishCV(
  userId: string,
  cvId: string,
  cv: CV,
  template: TemplateType,
  colorPalette: string,
  customSlug?: string
): Promise<PublicCV> {
  // Generate slug from name or use custom
  let slug = customSlug || slugify(cv.personalInfo?.fullName || 'cv')
  if (!slug) slug = 'cv'

  // Ensure slug is unique by appending random suffix if needed
  const isAvailable = await checkSlugAvailability(slug)
  if (!isAvailable) {
    slug = `${slug}-${nanoid(4)}`
  }

  const publicId = nanoid()
  const now = new Date()

  const publicCV: PublicCV = {
    publicId,
    userId,
    cvId,
    cv,
    slug,
    template,
    colorPalette,
    viewCount: 0,
    enabled: true,
    createdAt: now,
    updatedAt: now,
  }

  await setDoc(doc(db, COLLECTION, publicId), publicCV)
  return publicCV
}

/** Update a published CV */
export async function updatePublicCV(
  publicId: string,
  cv: CV,
  template: TemplateType,
  colorPalette: string
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, publicId), {
    cv,
    template,
    colorPalette,
    updatedAt: new Date(),
  })
}

/** Unpublish a CV */
export async function unpublishCV(publicId: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, publicId))
}

/** Get a public CV by slug */
export async function getPublicCVBySlug(slug: string): Promise<PublicCV | null> {
  const q = query(collection(db, COLLECTION), where('slug', '==', slug), where('enabled', '==', true))
  const snapshot = await getDocs(q)
  if (snapshot.empty) return null
  return snapshot.docs[0].data() as PublicCV
}

/** Get public CV by user's cvId */
export async function getPublicCVByCvId(userId: string, cvId: string): Promise<PublicCV | null> {
  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    where('cvId', '==', cvId)
  )
  const snapshot = await getDocs(q)
  if (snapshot.empty) return null
  return snapshot.docs[0].data() as PublicCV
}

/** Increment view count */
export async function incrementViewCount(publicId: string): Promise<void> {
  await updateDoc(doc(db, COLLECTION, publicId), {
    viewCount: increment(1),
  })
}

/** Get a public CV by its ID */
export async function getPublicCV(publicId: string): Promise<PublicCV | null> {
  const snap = await getDoc(doc(db, COLLECTION, publicId))
  if (!snap.exists()) return null
  return snap.data() as PublicCV
}
